import { userModelFields } from './unique-user-identifier-keys.js';
/**
 * Built-in user profile fields that can be required for redemptions.
 * These are a subset of UserModelField - excludes 'id' and 'identifierEmail'
 * since those are system-managed, not user-fillable profile fields.
 *
 * Used for:
 * - RedemptionType base required fields (RedemptionType.baseRequiredUserFields)
 * - Redemption specific required fields (Redemption.specificRequiredUserFields)
 * - User profile validation before redemption claims
 * - Form generation for missing field prompts
 */
// Fields that can be required for user profile validation (excludes system fields)
const builtInUserFieldsArray = [
    'email',
    'firstName',
    'lastName',
    'externalId',
    'instagramAccountId',
    'googleAccountName',
];
// Export the array for iteration
export const builtInUserFields = [...builtInUserFieldsArray];
export { userModelFields };
/**
 * Static metadata mapping for all built-in user fields.
 * Used by validation services and UI form generation.
 */
export const builtInUserFieldMetadata = {
    email: {
        label: 'Email Address',
        description: 'User email address',
        fieldType: 'email',
    },
    firstName: {
        label: 'First Name',
        description: 'User first name',
        fieldType: 'text',
    },
    lastName: {
        label: 'Last Name',
        description: 'User last name',
        fieldType: 'text',
    },
    externalId: {
        label: 'External ID',
        description: 'External identifier from third-party system',
        fieldType: 'text',
    },
    instagramAccountId: {
        label: 'Instagram Account',
        description: 'Instagram account ID',
        fieldType: 'text',
    },
    googleAccountName: {
        label: 'Google Account',
        description: 'Google account name',
        fieldType: 'text',
    },
};
/**
 * Check if a string is a valid built-in user field
 */
export const isBuiltInUserField = (value) => {
    return builtInUserFields.includes(value);
};
export const getBuiltInFieldDefinition = (field) => {
    if (!isBuiltInUserField(field)) {
        return null;
    }
    const metadata = builtInUserFieldMetadata[field];
    const sortOrder = builtInUserFields.indexOf(field);
    return {
        id: `builtin:${field}`,
        tenantId: null,
        entityType: 'user',
        key: field,
        label: metadata.label,
        description: metadata.description,
        fieldType: metadata.fieldType,
        selectOptions: null,
        selectOptionsSource: null,
        validation: null,
        sortOrder,
        createdAt: null,
        updatedAt: null,
        isBuiltIn: true,
    };
};
/**
 * Get all built-in field definitions as an array.
 * Useful for populating field definitions list.
 */
export const getAllBuiltInFieldDefinitions = () => {
    return builtInUserFields.map(field => getBuiltInFieldDefinition(field));
};
export { validateFieldValue, validateFieldComparisons, validateAllFields, compareValues, getOperatorText, } from '../validation/index.js';
/**
 * Entity types for custom fields and dynamic select options.
 * Used for both field definitions (entityType) and select option sources.
 * - 'user': User profile fields (stored in user.customData)
 * - 'business': Business profile fields (stored in business.customData)
 * - 'campaign': Campaign-specific custom fields
 * - 'redemption': Redemption-specific custom fields
 */
const customFieldEntityTypesArray = ['user', 'business', 'campaign', 'redemption'];
/** All supported custom field entity types for iteration/validation */
export const customFieldEntityTypes = [...customFieldEntityTypesArray];
/**
 * Entity types available for select options source (includes static sources)
 * - Database entities: user, business, campaign, redemption
 * - Static sources: country (ISO 3166-1 country list)
 */
const selectOptionsSourceEntityArray = [...customFieldEntityTypesArray, 'country'];
/** All supported select options source entities for iteration/validation */
export const selectOptionsSourceEntities = [...selectOptionsSourceEntityArray];
/**
 * Default label fields for each entity type.
 * Used when labelField is not explicitly specified.
 */
export const SELECT_OPTIONS_LABEL_DEFAULTS = {
    business: 'displayName',
    campaign: 'name',
    redemption: 'name',
    user: 'email', // User has no single 'name' field
    country: 'label', // Static source - uses 'label' from { value, label }
};
/**
 * Get the default label field for a select options entity.
 * Falls back to 'name' for unknown entities.
 */
export function getSelectOptionsLabelDefault(entity) {
    return SELECT_OPTIONS_LABEL_DEFAULTS[entity] ?? 'name';
}
//# sourceMappingURL=custom-fields.type.js.map